import React, { useEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { hardRedirect } from "./hardRedirect";

export function getHomePathForRole(userRole) {
  const role = Number(userRole);
  if (!Number.isFinite(role)) {
    return null;
  }
  return role === 8 ? "/home" : "/agent/dashboard";
}

export function isAgentRoute(pathname) {
  return pathname.startsWith("/agent");
}

const CLIENT_ROUTE_PREFIXES = [
  "/home",
  "/tp-studio",
  "/galaxy-casino",
  "/lucky7-galaxy",
  "/tp20-galaxy",
  "/hilo-galaxy",
  "/andarbahar-galaxy",
  "/dt-galaxy",
  "/luckyjet-galaxy",
  "/betfair",
  "/bet-history",
  "/accounts",
  "/auto-wallet",
  "/profile",
  "/results",
  "/current-position-list",
  "/royal-star",
  "/world-casino",
  "/royal-casino",
  "/multi-casino",
  "/sports-book",
];

export function isClientRoute(pathname) {
  return CLIENT_ROUTE_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );
}

export function isSharedRoute(pathname) {
  const sharedPrefixes = [
    "/market",
    "/plby-hierarchical",
    "/daily-pl-round-players",
    "/bet-details-view",
    "/virtual-details-view",
    "/pl-downline-view",
    "pl-downline-des",
    "/trans-page",
    "/transDetails",
    "/clinet-bet-details-view",
    "/client-virtual-details-view",
    "/client-tp-details-view",
    "/client-casino-details-view"
  ];

  return sharedPrefixes.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );
}

export function getRedirectForRole(pathname, userRole) {
  const role = Number(userRole);

  if (pathname === "/" || !Number.isFinite(role)) {
    return null;
  }

  if (isSharedRoute(pathname)) {
    return null;
  }

  if (role === 8) {
    return isAgentRoute(pathname) ? "/home" : null;
  }

  if (isClientRoute(pathname)) {
    return "/agent/dashboard";
  }

  return null;
} 

// Require login for all except login page
export function RequireAuth({ children }) {
  const { data: user, initialized, loading } = useSelector((s) => s.userStore);

  useEffect(() => {
    if (initialized && !loading && !user) {
      hardRedirect("/");
    }
  }, [user, initialized, loading]);

  if (!initialized || loading) {
    return null;
  }

  if (!user) {
    return null;
  }

  return children ? children : <Outlet />;
}

// Role 8 uses non-/agent routes; all other roles use /agent/* routes
export function RoleAreaGuard({ children }) {
  const { data: user, initialized, loading } = useSelector((s) => s.userStore);
  const location = useLocation();
  const navigate = useNavigate();

  const redirect =
    initialized && !loading
      ? getRedirectForRole(location.pathname, user?.user_role)
      : null;

  // Use navigate (SPA transition) so Redux stays intact and the target page
  // renders immediately — hardRedirect would wipe the store and cause a blank.
  useEffect(() => {
    if (redirect) {
      navigate(redirect, { replace: true });
    }
  }, [redirect, navigate]);

  if (!initialized || loading) {
    return null;
  }

  if (redirect) {
    return null;
  }

  return children ? children : <Outlet />;
}

// TP Studio and other punter-only areas (role 8)
export function ClientOnlyGuard({ children }) {
  const { data: user, initialized, loading } = useSelector((s) => s.userStore);
  const role = Number(user?.user_role);
  const navigate = useNavigate();

  useEffect(() => {
    if (initialized && !loading && Number.isFinite(role) && role !== 8) {
      navigate("/agent/dashboard", { replace: true });
    }
  }, [initialized, loading, role, navigate]);

  if (!initialized || loading) {
    return null;
  }

  if (!Number.isFinite(role) || role !== 8) {
    return null;
  }

  return children ? children : <Outlet />;
}

export function AutoWalletGuard({ children }) {
  const { data: user, initialized, loading } = useSelector((s) => s.userStore);
  const navigate = useNavigate();
  const allowed = !!user?.upline_allow_auto_deposit;

  useEffect(() => {
    if (initialized && !loading && !allowed) {
      navigate("/home", { replace: true });
    }
  }, [initialized, loading, allowed, navigate]);

  if (!initialized || loading) {
    return null;
  }

  if (!allowed) {
    return null;
  }

  return children ? children : <Outlet />;
}
